import * as React from "react";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import type { ReleaseNote, ReleaseNoteAudience, ReleaseNoteKind } from "@/api/types";
import { ArrowForwardOutlined } from "@/components/icons";

// Literal t() calls (not a template key) so keys.test.ts sees them.
function kindLabel(t: TFunction, kind: ReleaseNoteKind): string {
  switch (kind) {
    case "new":
      return t("whats_new.kind_new");
    case "changed":
      return t("whats_new.kind_changed");
    case "improved":
      return t("whats_new.kind_improved");
    case "fixed":
      return t("whats_new.kind_fixed");
  }
}

function audienceLabel(t: TFunction, audience: ReleaseNoteAudience): string | null {
  switch (audience) {
    case "admins":
      return t("whats_new.audience_admins");
    case "platform":
      return t("whats_new.audience_platform");
    case "everyone":
      return null;
  }
}

/** A kind's accent is only a border: the chip's text says the kind, so
 *  colour never carries it alone, and the text keeps full contrast. */
const KIND_ACCENT: Record<ReleaseNoteKind, string> = {
  new: "success.main",
  changed: "warning.main",
  improved: "info.main",
  fixed: "divider",
};

type Props = {
  note: ReleaseNote;
  /** Show the body and the "Show me" action (highlights, the page). */
  detailed?: boolean;
  /** "Show me" hands the link over (the dialog closes first); without
   *  it the action is a plain in-app link. */
  onShowMe?: (link: string) => void;
  component?: React.ElementType;
};

/** One release note, the same in the dialog and on the page: the kind
 *  and (for admin notes) the audience as text chips, the title, and in
 *  detailed mode the body and "Show me". Plain text only: notes have no
 *  markup. Text shown in a fallback language carries its own ``lang``. */
export function ReleaseNoteItem({
  note,
  detailed = false,
  onShowMe,
  component = "div",
}: Props): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const ownLang = note.lang !== (i18n.resolvedLanguage ?? "en") ? note.lang : undefined;
  const audience = audienceLabel(t, note.audience);
  const showMe = detailed && note.link ? note.link : null;
  return (
    <Box component={component} data-note-id={note.id} sx={{ minWidth: 0 }}>
      <Stack direction="row" sx={{ gap: 1, alignItems: "baseline", flexWrap: "wrap" }}>
        <Chip
          size="small"
          variant="outlined"
          label={kindLabel(t, note.kind)}
          sx={{
            fontWeight: 600,
            borderColor: KIND_ACCENT[note.kind],
            borderWidth: 1.5,
            flexShrink: 0,
          }}
        />
        {audience && <Chip size="small" label={audience} sx={{ flexShrink: 0 }} />}
        <Typography
          component="span"
          variant="body2"
          lang={ownLang}
          sx={{ fontWeight: 600, minWidth: 0, flex: "1 1 12rem", overflowWrap: "anywhere" }}
        >
          {note.title}
        </Typography>
      </Stack>
      {detailed && note.body && (
        <Typography
          variant="body2"
          lang={ownLang}
          sx={{ color: "text.secondary", mt: 0.75, overflowWrap: "anywhere" }}
        >
          {note.body}
        </Typography>
      )}
      {showMe && (
        <Box sx={{ mt: 0.5, display: "flex", justifyContent: "flex-end" }}>
          <Button
            size="small"
            endIcon={<ArrowForwardOutlined aria-hidden />}
            aria-label={t("whats_new.show_me_label", { title: note.title })}
            sx={{ minHeight: 44 }}
            {...(onShowMe
              ? { onClick: () => onShowMe(showMe) }
              : { component: RouterLink, to: showMe })}
          >
            {t("whats_new.show_me")}
          </Button>
        </Box>
      )}
    </Box>
  );
}
